import type { HTMLAttributes, ReactNode } from "react";

type CardPadding = "none" | "xs" | "sm" | "md" | "lg" | "xl";
type CardVariant = "surface" | "raised" | "quiet" | "hero" | "danger" | "glass";

type CardProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  padding?: CardPadding;
  variant?: CardVariant;
  accentEdge?: "left" | "top";
  tone?: "default" | "accent" | "warning" | "danger" | "success" | "ember";
  interactive?: boolean;
  pattern?: "topo" | "aurora" | "none";
};

const paddingClass: Record<CardPadding, string> = {
  none: "p-0",
  xs: "p-3",
  sm: "p-4",
  md: "p-5",
  lg: "p-6 md:p-8",
  xl: "p-8 md:p-10 lg:p-12",
};

const canonicalClass: Record<CardVariant, string> = {
  surface: "card",
  raised: "card-elevated",
  quiet: "surface-quiet",
  hero: "card-elevated hero-card",
  danger: "card tone-danger",
  glass: "card-glass",
};

const toneClass = {
  default: "",
  accent: "tone-accent",
  warning: "tone-warning",
  danger: "tone-danger",
  success: "tone-success",
  ember: "tone-warning",
};

export function Card({
  children,
  footer,
  header,
  className = "",
  padding = "md",
  variant = "surface",
  accentEdge,
  tone = "default",
  interactive = false,
  pattern = "none",
  ...props
}: CardProps) {
  const patternClass = pattern === "topo" ? "hero-topo" : pattern === "aurora" ? "hero-aurora" : "";
  const interactiveClass = interactive ? "card-interactive" : "";
  const edgeTone =
    tone === "danger" || tone === "warning" || tone === "ember"
      ? "bg-[var(--gradient-ember)]"
      : "bg-[var(--gradient-brand)]";
  const edge =
    accentEdge === "left" ? (
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-2 left-0 w-[3px] rounded-pill ${edgeTone}`}
      />
    ) : accentEdge === "top" ? (
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-2 top-0 h-[3px] rounded-pill ${edgeTone}`}
      />
    ) : null;

  return (
    <div
      className={`relative transition-[border-color,box-shadow,transform,background-color] duration-200 ease-out ${canonicalClass[variant]} ${toneClass[tone]} ${patternClass} ${interactiveClass} ${paddingClass[padding]} ${className}`}
      {...props}
    >
      {edge}
      {header ? <div className="relative z-10">{header}</div> : null}
      {children}
      {footer ? (
        <div className="relative z-10 mt-4 border-t border-border pt-4">{footer}</div>
      ) : null}
    </div>
  );
}
